import React, { useState } from 'react';
import { Box, Chip, TextField, InputAdornment, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
import { useToast } from './ui/ToastProvider';
interface ScreeningInfo {
  engine: string;
  score: number;
  hits: string[];
  failReasonCode: string;
  failReasonText: string;
  screenedAt: string;
}
const MTO_NAMES = ['RIA Money Transfer', 'InstantCash', 'MoneyGram', 'Western Union', 'Xpress Money'];
const AGENT_CODES = ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'];
const BRANCH_CODES = ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'];
export interface ComplianceTransaction {
  txnId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  beneficiaryName: string;
  beneficiaryCnic: string;
  beneficiaryTitle: string;
  amountFcy: string;
  currencyFcy: string;
  amountPkr: string;
  createdAt: string;
  lastUpdateAt: string;
  lifecycle: 'IN_REVIEW';
  currentState: 'SCREENING';
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  railRefs: {
    RaastMsgId?: string;
    RRN?: string;
    STAN?: string;
    UETR?: string;
  };
  screening: {
    engine: string;
    score: number;
    hits: string[];
    failReasonCode: string;
    failReasonText: string;
    screenedAt: string;
  };
  remitterName: string;
  remitterIdType: string;
  remitterIdNumber: string;
  remitterCountry: string;
  beneficiaryIban?: string;
  beneficiaryBank?: string;
  purposeCode: string;
  purposeText: string;
  fxRate: number;
}
interface ComplianceTableProps {
  transactions: ComplianceTransaction[];
  onRowClick: (transaction: ComplianceTransaction) => void;
  onExport: () => void;
}
const ComplianceTable: React.FC<ComplianceTableProps> = ({
  transactions,
  onRowClick,
  onExport
}) => {
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAmendTransaction, setSelectedAmendTransaction] = useState<ComplianceTransaction | null>(null);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<ComplianceTransaction | null>(null);
  const {
    showToast
  } = useToast();
  const [filters, setFilters] = useState({
    txnId: '',
    mtoName: '',
    payoutType: '',
    beneficiaryName: '',
    beneficiaryCnic: '',
    beneficiaryTitle: '',
    branchCode: '',
    agentCode: [] as string[],
    amountFcyMin: '',
    amountFcyMax: '',
    amountPkrMin: '',
    amountPkrMax: '',
    dateFrom: '',
    dateTo: ''
  });
  const [dateRangeError, setDateRangeError] = useState<string>('');
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    // Check if both dates are selected and validate
    if (newFilters.dateFrom && newFilters.dateTo) {
      if (!validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
        setDateRangeError('❌ Date range cannot exceed 3 months');
        return;
      }
    }
    // Clear error if validation passes
    setDateRangeError('');
    setFilters(newFilters);
  };
  const handleAmendClick = (transaction: ComplianceTransaction) => {
    setSelectedAmendTransaction(transaction);
    setAmendDialogOpen(true);
  };
  const handleAmendConfirm = async (reason: string) => {
    if (!selectedAmendTransaction) return;
    showToast(`Amendment request submitted for ${selectedAmendTransaction.txnId}`, 'success');
    setAmendDialogOpen(false);
    setSelectedAmendTransaction(null);
  };
  const handleCancelClick = (transaction: ComplianceTransaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedCancelTransaction) return;
    showToast(`Cancellation request submitted for ${selectedCancelTransaction.txnId}`, 'success');
    setCancelDialogOpen(false);
    setSelectedCancelTransaction(null);
  };
  // Define columns
  const columns: Column<ComplianceTransaction>[] = [{
    id: 'txnId',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" data-id="element-875" data-mp="2dn2v4uywullq">
          {row.txnId}
        </Typography>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-876" data-mp="2hoxy03whp71m">
          <Select value={filters.txnId || 'all'} onChange={e => setFilters({
        ...filters,
        txnId: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-877" data-mp="60t34kb9zjqi">
            <MenuItem value="all" data-id="element-878" data-mp="3o90mxwcc29vu">All</MenuItem>
            {Array.from(new Set(transactions.map(t => t.txnId))).map(id => <MenuItem key={id} value={id} data-id="element-879" data-mp="kpfhaip625g5">
                {id}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'agentCode',
    label: 'Agent Code',
    renderCell: row => <Chip label={row.agentCode || ''} size="small" sx={{
      backgroundColor: 'rgba(79, 70, 229, 0.1)',
      color: '#4F46E5',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.agentCode ? 'visible' : 'hidden'
    }} data-id="element-880" data-mp="azvvb465pvh2" />,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-881" data-mp="31yiel3e1bdgz">
          <Select multiple value={filters.agentCode} onChange={e => setFilters({
        ...filters,
        agentCode: e.target.value as string[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-882" data-mp="320d1gn8v9fnp">
            {AGENT_CODES.map(code => <MenuItem key={code} value={code} data-id="element-883" data-mp="2a6kphuz82gae">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderCell: row => <Chip label={row.branchCode || ''} size="small" sx={{
      backgroundColor: 'rgba(6, 182, 212, 0.1)',
      color: '#06B6D4',
      fontWeight: 600,
      fontSize: '0.7rem',
      visibility: row.branchCode ? 'visible' : 'hidden'
    }} data-id="element-884" data-mp="8ey6aflknpoh" />,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.branchCode} onChange={e => setFilters({
      ...filters,
      branchCode: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-886" data-mp="169icwfr55c1v">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-887" data-mp="169qqk9v38le4" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-885" data-mp="mkzrm999aisg" />
  }, {
    id: 'mtoName',
    label: 'Remitting Institute',
    renderCell: row => <Typography variant="body2" fontWeight={500} data-id="element-888" data-mp="ebk7sjxwk78">
          {row.mtoName}
        </Typography>,
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-889" data-mp="a5pf1n0y4vu1">
          <Select value={filters.mtoName || 'all'} onChange={e => setFilters({
        ...filters,
        mtoName: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-890" data-mp="a6r0mlfl90q0">
            <MenuItem value="all" data-id="element-891" data-mp="3mlt0rcb8wtow">All</MenuItem>
            {Array.from(new Set(transactions.map(t => t.mtoName))).map(name => <MenuItem key={name} value={name} data-id="element-892" data-mp="11xdnvzkin084">
                  {name}
                </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => {
      const colors = {
        FT: {
          bg: 'rgba(14, 165, 233, 0.1)',
          color: '#0EA5E9'
        },
        IBFT: {
          bg: 'rgba(139, 92, 246, 0.1)',
          color: '#8B5CF6'
        },
        OTC: {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        }
      };
      const style = colors[row.payoutType];
      return <Chip label={row.payoutType} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-893" data-mp="y7kqk2v9vgy9" />;
    },
    renderFilter: () => <FormControl size="small" fullWidth data-id="element-894" data-mp="kc8v1t9almga">
          <Select value={filters.payoutType || 'all'} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-895" data-mp="2ioh7x17x8wnv">
            <MenuItem value="all" data-id="element-896" data-mp="23vgrdiwka95t">All</MenuItem>
            <MenuItem value="FT" data-id="element-897" data-mp="23t4brcea0avx">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-898" data-mp="1yvm2tm09dvnx">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-899" data-mp="1ywx6lltpf3f1">OTC</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary Name',
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryName} onChange={e => setFilters({
      ...filters,
      beneficiaryName: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-901" data-mp="ngfic909g73h">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-902" data-mp="2z4kc1p9qnod0" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-900" data-mp="1699k218vi2pr" />
  }, {
    id: 'beneficiaryCnic',
    label: 'Beneficiary CNIC',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-903" data-mp="2u68z35o5cxqk">
          {row.beneficiaryCnic}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryCnic} onChange={e => setFilters({
      ...filters,
      beneficiaryCnic: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-905" data-mp="22e1lke3xu1ge">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-906" data-mp="22et6muzwsgsf" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-904" data-mp="3mtmugzc75l8k" />
  }, {
    id: 'beneficiaryTitle',
    label: 'Beneficiary Title',
    renderCell: row => <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" data-id="element-907" data-mp="3ub1f136b97o9">
          {row.beneficiaryTitle}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.beneficiaryTitle} onChange={e => setFilters({
      ...filters,
      beneficiaryTitle: e.target.value
    })} sx={{
      width: '100%'
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-909" data-mp="19m5ce6nc53l3">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-910" data-mp="19ku7g3i5lpse" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-908" data-mp="3leam2zn228xw" />
  }, {
    id: 'amountFcy',
    label: 'Amount FCY',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-911" data-mp="2x8hs80dz70au">
          {row.currencyFcy} {row.amountFcy}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-912" data-mp="319to2nng0hrh">
          <TextField size="small" placeholder="Min" value={filters.amountFcyMin} onChange={e => setFilters({
        ...filters,
        amountFcyMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-913" data-mp="29j5imzeqkq80" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-914" data-mp="f9i8sij6ig1v" />
          <TextField size="small" placeholder="Max" value={filters.amountFcyMax} onChange={e => setFilters({
        ...filters,
        amountFcyMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-915" data-mp="abp4xw5cly4r" />
        </Box>
  }, {
    id: 'amountPkr',
    label: 'Amount PKR',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-916" data-mp="k0vt60y41jzz">
          PKR {row.amountPkr}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-917" data-mp="ozgsly5jfznd">
          <TextField size="small" placeholder="Min" value={filters.amountPkrMin} onChange={e => setFilters({
        ...filters,
        amountPkrMin: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-918" data-mp="fy960dcdwn94" />
          <Icon icon="lucide:arrow-right" width={16} height={16} data-id="element-919" data-mp="1g4mdrspha20z" />
          <TextField size="small" placeholder="Max" value={filters.amountPkrMax} onChange={e => setFilters({
        ...filters,
        amountPkrMax: e.target.value
      })} inputProps={{
        type: 'number'
      }} sx={{
        width: '50%',
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0EA5E9',
            boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
          }
        }
      }} InputProps={{
        sx: {
          height: 36
        }
      }} data-id="element-920" data-mp="1b6t9x6bndk3v" />
        </Box>
  }, {
    id: 'createdAt',
    label: 'Created',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" data-id="element-921" data-mp="26afibnh96nne">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5
    }} data-id="element-922" data-mp="21a1fqkyf85wr">
          <Box sx={{
        display: 'flex',
        gap: 0.5
      }} data-id="element-923" data-mp="2b1feurmgn3pd">
            <TextField size="small" type="date" value={filters.dateFrom} onChange={e => handleDateChange('dateFrom', e.target.value)} error={!!dateRangeError} inputProps={{
          max: filters.dateTo ? new Date(new Date(filters.dateTo).setMonth(new Date(filters.dateTo).getMonth() - 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-924" data-mp="1ncw5e8rockvp" />
            <TextField size="small" type="date" value={filters.dateTo} onChange={e => handleDateChange('dateTo', e.target.value)} error={!!dateRangeError} inputProps={{
          min: filters.dateFrom ? new Date(new Date(filters.dateFrom).setMonth(new Date(filters.dateFrom).getMonth() + 3)).toISOString().split('T')[0] : undefined
        }} sx={{
          width: '50%',
          '& .MuiOutlinedInput-root': {
            fontSize: '0.8rem'
          }
        }} InputProps={{
          sx: {
            height: 36
          }
        }} data-id="element-925" data-mp="36b136fompzei" />
          </Box>
          {dateRangeError && <Typography variant="caption" color="error" sx={{
        fontSize: '0.7rem',
        mt: 0.5
      }} data-id="element-926" data-mp="28e4ndkio5107">
              {dateRangeError}
            </Typography>}
        </Box>
  }, {
    id: 'lastUpdateAt',
    label: 'Last Update',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" data-id="element-927" data-mp="1pfjvhq8ydrli">
          {new Date(row.lastUpdateAt).toLocaleString()}
        </Typography>,
    renderFilter: () => <Box sx={{
      height: 36
    }} data-id="element-928" data-mp="3et5hrls1pb4o" />
  }];
  // Apply filters
  const filteredTransactions = transactions.filter(txn => {
    if (filters.txnId && !txn.txnId.includes(filters.txnId)) return false;
    if (filters.mtoName && txn.mtoName !== filters.mtoName) return false;
    if (filters.payoutType && txn.payoutType !== filters.payoutType) return false;
    if (filters.beneficiaryName && !txn.beneficiaryName.toLowerCase().includes(filters.beneficiaryName.toLowerCase())) return false;
    if (filters.beneficiaryCnic && !txn.beneficiaryCnic.includes(filters.beneficiaryCnic)) return false;
    if (filters.beneficiaryTitle && !txn.beneficiaryTitle.includes(filters.beneficiaryTitle)) return false;
    // Agent Code filter
    if (filters.agentCode.length > 0) {
      if (!filters.agentCode.includes(txn.agentCode)) return false;
    }
    // Branch Code filter
    if (filters.branchCode && !txn.branchCode.toLowerCase().includes(filters.branchCode.toLowerCase())) {
      return false;
    }
    // Amount FCY filter
    if (filters.amountFcyMin || filters.amountFcyMax) {
      const amount = parseFloat(txn.amountFcy);
      if (filters.amountFcyMin && amount < parseFloat(filters.amountFcyMin)) return false;
      if (filters.amountFcyMax && amount > parseFloat(filters.amountFcyMax)) return false;
    }
    // Amount PKR filter
    if (filters.amountPkrMin || filters.amountPkrMax) {
      const amount = parseFloat(txn.amountPkr.replace(/,/g, ''));
      if (filters.amountPkrMin && amount < parseFloat(filters.amountPkrMin)) return false;
      if (filters.amountPkrMax && amount > parseFloat(filters.amountPkrMax)) return false;
    }
    if (filters.dateFrom) {
      const txnDate = new Date(txn.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (txnDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const txnDate = new Date(txn.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (txnDate > toDate) return false;
    }
    return true;
  });
  const filterChips: FilterChip[] = [...(filters.txnId ? [{
    id: 'txnId',
    label: 'Txn ID',
    value: filters.txnId,
    onDelete: () => setFilters({
      ...filters,
      txnId: ''
    })
  }] : []), ...(filters.agentCode.length > 0 ? [{
    id: 'agentCode',
    label: 'Agent Code',
    value: filters.agentCode.join(', '),
    onDelete: () => setFilters({
      ...filters,
      agentCode: []
    })
  }] : []), ...(filters.branchCode ? [{
    id: 'branchCode',
    label: 'Branch Code',
    value: filters.branchCode,
    onDelete: () => setFilters({
      ...filters,
      branchCode: ''
    })
  }] : []), ...(filters.mtoName ? [{
    id: 'mtoName',
    label: 'MTO',
    value: filters.mtoName,
    onDelete: () => setFilters({
      ...filters,
      mtoName: ''
    })
  }] : []), ...(filters.payoutType ? [{
    id: 'payoutType',
    label: 'Type',
    value: filters.payoutType,
    onDelete: () => setFilters({
      ...filters,
      payoutType: ''
    })
  }] : []), ...(filters.beneficiaryName ? [{
    id: 'beneficiaryName',
    label: 'Beneficiary',
    value: filters.beneficiaryName,
    onDelete: () => setFilters({
      ...filters,
      beneficiaryName: ''
    })
  }] : []), ...(filters.amountFcyMin || filters.amountFcyMax ? [{
    id: 'amountFcy',
    label: 'Amount FCY',
    value: filters.amountFcyMin && filters.amountFcyMax ? `${filters.amountFcyMin} - ${filters.amountFcyMax}` : filters.amountFcyMin ? `≥ ${filters.amountFcyMin}` : `≤ ${filters.amountFcyMax}`,
    onDelete: () => setFilters({
      ...filters,
      amountFcyMin: '',
      amountFcyMax: ''
    })
  }] : []), ...(filters.amountPkrMin || filters.amountPkrMax ? [{
    id: 'amountPkr',
    label: 'Amount PKR',
    value: filters.amountPkrMin && filters.amountPkrMax ? `${filters.amountPkrMin} - ${filters.amountPkrMax}` : filters.amountPkrMin ? `≥ ${filters.amountPkrMin}` : `≤ ${filters.amountPkrMax}`,
    onDelete: () => setFilters({
      ...filters,
      amountPkrMin: '',
      amountPkrMax: ''
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : [])];
  const rowActions: RowAction<ComplianceTransaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => onRowClick(row)
  }, {
    icon: 'lucide:edit',
    label: 'Amend',
    onClick: row => handleAmendClick(row),
    condition: (row: any) => row.amendAllowed === true && (!row.amendStatus || row.amendStatus === 'NONE' || row.amendStatus === 'REJECTED')
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => handleCancelClick(row),
    condition: (row: any) => row.cancelAllowed === true && (!row.cancelStatus || row.cancelStatus === 'NONE' || row.cancelStatus === 'REJECTED')
  }];
  return <>
      <DataTable title={`Compliance Review (${filteredTransactions.length} Pending)`} data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="txnId" filterChips={filterChips} onClearAllFilters={() => setFilters({
      txnId: '',
      mtoName: '',
      payoutType: '',
      beneficiaryName: '',
      beneficiaryCnic: '',
      beneficiaryTitle: '',
      branchCode: '',
      agentCode: [],
      amountFcyMin: '',
      amountFcyMax: '',
      amountPkrMin: '',
      amountPkrMax: '',
      dateFrom: '',
      dateTo: ''
    })} onExport={onExport} emptyStateMessage="No transactions awaiting screening" onRowClick={onRowClick} data-id="element-929" data-mp="m5wbcoev2hgk" />

      {selectedAmendTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedAmendTransaction(null);
    }} onConfirm={handleAmendConfirm} transactionId={selectedAmendTransaction.txnId} data-id="element-930" data-mp="18d8izwygf7dj" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedCancelTransaction.txnId} cancellationAction={(selectedCancelTransaction as any).cancellationAction || 'SIMPLE_CANCEL'} data-id="element-931" data-mp="kns45k87hea8" />}
    </>;
};
export default ComplianceTable;